import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import AutoScroll from 'embla-carousel-auto-scroll';
import ClassNames from 'embla-carousel-class-names';
import AutoHeight from 'embla-carousel-auto-height';
import Fade from 'embla-carousel-fade';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { addThumbBtnsClickHandlers, addToggleThumbBtnsActive } from './utils/thumbnails.js';
import { setupFractionCounter } from './utils/fraction.js';
import { setupProgressBar } from './utils/progressbar.js';
import { arrowSvgs } from './utils/arrowIcons.js';
import { BREAKPOINTS } from './utils/breakpoints';
import { measureDotsHeight } from './utils/measureDotsHeight.js';
import { applyInitialFetchPriority } from './utils/update-image-fetch-priority.js';
import initializeDynamicSlider from './utils/dynamic-slider.js';
import { __, sprintf } from '@wordpress/i18n';


const rootApiUrl = window?.wpmundi_slider_params?.root_api_url;

if (!rootApiUrl) {
  console.error('root_api_url não está disponível. Verifique wp_localize_script.');
} 

// --- FUNÇÃO: AUTO PAUSE DE VÍDEO ---
// Pausa vídeos HTML5 e Iframes (YouTube/Vimeo) quando o slide muda
const setupVideoAutopause = (emblaApi) => {
    emblaApi.on('select', () => {
        const slides = emblaApi.slideNodes();
        const currentIndex = emblaApi.selectedScrollSnap();

        slides.forEach((slide, index) => {
            // Se o slide não for o atual, pausa o conteúdo
            if (index !== currentIndex) {
                
                // 1. Pausa Tags <video> Nativas
                const videoTags = slide.querySelectorAll('video');
                videoTags.forEach((video) => {
                    if (!video.paused) {
                        video.pause();
                    }
                });

                // 2. Pausa Iframes (YouTube / Vimeo)
                const iframes = slide.querySelectorAll('iframe');
                iframes.forEach((iframe) => {
                    // YouTube
                    iframe.contentWindow.postMessage(
                        JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*'
                    );
                    // Vimeo
                    iframe.contentWindow.postMessage(
                        JSON.stringify({ method: 'pause' }), '*'
                    );
                });
            }
        });
    });
};

// --- NOVA FUNÇÃO AUXILIAR ---
// Esta função verifica o tamanho da tela e o data-attribute correspondente.
const shouldShowNavigation = (sliderNode, controlType) => {
    const screenWidth = window.innerWidth;
    if (screenWidth >= BREAKPOINTS.large) { // Desktop
        return sliderNode.dataset[`${controlType}Large`] === 'true';
    }
    if (screenWidth >= BREAKPOINTS.medium) { // Tablet
        return sliderNode.dataset[`${controlType}Medium`] === 'true';
    }
    // Mobile
    return sliderNode.dataset[`${controlType}Small`] === 'true';
};

// Função auxiliar para configurar os botões de navegação.
const setupButtons = (emblaApi, prevBtn, nextBtn) => {
	const updateButtons = () => {
		const canScrollPrev = emblaApi.canScrollPrev();
		const canScrollNext = emblaApi.canScrollNext();
		prevBtn.disabled = !canScrollPrev;
		nextBtn.disabled = !canScrollNext;
	};

	prevBtn.addEventListener('click', () => emblaApi.scrollPrev(), false);
	nextBtn.addEventListener('click', () => emblaApi.scrollNext(), false);

	emblaApi.on('select', updateButtons).on('reInit', updateButtons);
	updateButtons(); // Chama na inicialização
};


// SUA FUNÇÃO 'setupDots' RENOMEADA E AJUSTADA
const setupSimpleDots = (emblaApi, dotsNode, slides, dotType) => {
    dotsNode.innerHTML = '';
    let dotButtons = [];
    emblaApi.scrollSnapList().forEach((_, index) => {
        const dotBtn = document.createElement('button');
        dotBtn.className = `embla__dot dot-${dotType}`;

        dotBtn.setAttribute('type', 'button');
        const ariaLabel = sprintf(
            __('Go to slide %s', 'wpmundi-blocks'), 
            index + 1
        );
        dotBtn.setAttribute('aria-label', ariaLabel);

        dotBtn.addEventListener('click', () => emblaApi.scrollTo(index), false);
        dotsNode.appendChild(dotBtn);
        dotButtons.push(dotBtn);
    });

    const toggleDotBtnsActive = () => {
        const selected = emblaApi.selectedScrollSnap();
        dotButtons.forEach((dotBtn, i) => dotBtn.classList.toggle('embla__dot--selected', i === selected));
    };
    emblaApi.on('select', toggleDotBtnsActive).on('reInit', toggleDotBtnsActive);
    toggleDotBtnsActive();
};


// NOVA FUNÇÃO para o CARROSSEL DE NAVEGAÇÃO
const setupThumbCarousel = (emblaApi, thumbNode, slides, dotType, isLoop, isVertical, thumbsLoop) => {
    thumbNode.innerHTML = `<div class="embla__viewport thumb"><div class="embla__container thumb"></div></div>`;
    const thumbViewport = thumbNode.querySelector('.embla__viewport.thumb');
    const thumbContainer = thumbNode.querySelector('.embla__container.thumb');

    slides.forEach((slide, index) => {
        const thumbBtn = document.createElement('button');
        thumbBtn.className = `embla__slide embla__dot dot-${dotType}`;
        thumbBtn.setAttribute('type', 'button');

        let ariaLabel = '';
        const slideText = slide.dataset.thumbText;

        if (dotType === 'text' && slideText) {
            // Tradução: "Go to slide: About Us"
            ariaLabel = sprintf(
                __('Go to slide: %s', 'wpmundi-blocks'), 
                slideText
            );
        } else {
            // Padrão para Imagens ou se não houver texto: "Go to slide 1"
            ariaLabel = sprintf(
                __('Go to slide %s', 'wpmundi-blocks'), 
                index + 1
            );
        }
        thumbBtn.setAttribute('aria-label', ariaLabel);
        // ---------------------------------------------

        let content = '';
        if (dotType === 'image' && slide.dataset.thumbImageUrl) {
            content = `<div class="embla__dot__card"><img class="embla__dot__image" src="${slide.dataset.thumbImageUrl}" alt="" /></div>`;
        } else if (dotType === 'text' && slideText) {
            content = `<span class="embla__dot__text">${slideText}</span>`;
        }
        thumbBtn.innerHTML = content;
        thumbContainer.appendChild(thumbBtn);
    });

    const enableThumbLoop = thumbsLoop && !isVertical;

    const thumbEmblaApi = EmblaCarousel(thumbViewport, {
        axis: isVertical ? 'y' : 'x',

        /* containScroll: !isVertical && isLoop ? false : 'keepSnaps', */ 
          
        containScroll: enableThumbLoop ? false : 'keepSnaps',
        dragFree: true,		
        align: 'center',
        /* loop: isVertical ? false : isLoop, */
        loop: enableThumbLoop,
        friction: 0.8,
    },
    [ WheelGesturesPlugin({ forceWheelAxis: isVertical ? 'y' : 'x', wheelFreeScroll: true, factor: 8,  }) ]
    );
    

    addThumbBtnsClickHandlers(emblaApi, thumbEmblaApi);
    addToggleThumbBtnsActive(emblaApi, thumbEmblaApi);
};


// --- NOVA FUNÇÃO REUTILIZÁVEL PARA OS DOTS ---
function updateDots(emblaApi, sliderNode) {
    // Tenta encontrar e remover containers de dots existentes para evitar duplicação
    const existingDots = sliderNode.querySelector('.embla__dots');
    if (existingDots) existingDots.remove();
    
    // As variáveis são locais para esta função
    let dotsContainer, thumbNav, fractionCounter;

    if (shouldShowNavigation(sliderNode, 'showDots')) {
        const dotsType = sliderNode.dataset.dotsType || 'default';
        const slides = emblaApi.slideNodes();
        
        // Orientação das thumbs
        const requestedOrientation = sliderNode.dataset.thumbsOrientation || 'horizontal';
        const isDesktop = window.innerWidth >= BREAKPOINTS.large;
        let effectiveOrientation = requestedOrientation;

        if (requestedOrientation === 'vertical' && !isDesktop) {
            effectiveOrientation = 'horizontal';
        }

        const isVertical = effectiveOrientation === 'vertical';
        const orientationClass = `is-${effectiveOrientation}`;        

        if (dotsType === 'image' || dotsType === 'text') {
            thumbNav = document.createElement('div');
            thumbNav.className = `embla__dots embla embla-thumbs embla__dots--${dotsType} ${orientationClass}`;
            sliderNode.appendChild(thumbNav);
            const isLoop = sliderNode.dataset.loop === 'true';
            const thumbsLoop = sliderNode.dataset.thumbsLoop === 'true';
            setupThumbCarousel(emblaApi, thumbNav, slides, dotsType, isLoop, isVertical, thumbsLoop);
        } else if (dotsType === 'fraction') {
            // 1. Cria a div PAI (a que posiciona o elemento na tela)
            fractionCounter = document.createElement('div');
            fractionCounter.className = `embla__dots embla__dots--${dotsType}`;
            
            // 2. Cria a nova div CONTAINER (para estilização interna)
            const fractionInnerContainer = document.createElement('div');
            fractionInnerContainer.className = 'embla__fraction__container';
            
            // 3. Coloca o container dentro da div pai
            fractionCounter.appendChild(fractionInnerContainer);
            
            // 4. Adiciona ao slider
            sliderNode.appendChild(fractionCounter);
            
            // 5. IMPORTANTE: Passa o 'fractionInnerContainer' para a função de setup.
            // Assim, os spans (1/11) serão criados DENTRO do container, e não na div pai.
            setupFractionCounter(emblaApi, fractionInnerContainer);
        } else { // 'default'
            dotsContainer = document.createElement('div');
            dotsContainer.className = `embla__dots embla__dots--${dotsType}`;
            sliderNode.appendChild(dotsContainer);
            setupSimpleDots(emblaApi, dotsContainer, slides, dotsType);
        }
    }
}


// ---- FUNÇÃO PRINCIPAL QUE INICIALIZA OS SLIDERS NA PÁGINA ---
const initializeWPMundiSliders = () => {
	const sliderNodes = document.querySelectorAll('.wp-block-wpmundi-slider.embla');

	sliderNodes.forEach((sliderNode) => {
		const viewportNode = sliderNode.querySelector('.embla__viewport');
		if (!viewportNode) return;
				

		// 1. Lê todas as configurações dos data-attributes
		const options = {
            // --- OPÇÕES JÁ IMPLEMENTADAS ---
			loop: sliderNode.dataset.loop === 'true',
            draggable: sliderNode.dataset.draggable === 'true',
			align: sliderNode.dataset.alignSlide || 'start',
			axis: sliderNode.dataset.axis || 'x',
			duration: parseInt(sliderNode.dataset.duration, 10) || 25,
			slidesToScroll: parseInt(sliderNode.dataset.slidesToScrollSmall, 10) || 1,

            // --- OPÇÕES PARA FUTURAS IMPLEMENTAÇÕES (KANBAN) ---

            // Define o slide inicial (começa em 0).
            // TODO: Adicionar controle numérico "Slide Inicial".
            startIndex: 0,

            // Controla a velocidade da animação (não a duração da física).
            // Afeta a velocidade do clique nas setas e dots.
            // TODO: Adicionar RangeControl "Velocidade da Animação".
            speed: 10,

            // Permite um scroll "livre", sem "travar" nos slides.
            // Bom para carrosséis de logos ou galerias contínuas.
            // TODO: Adicionar ToggleControl "Scroll Livre".
            dragFree: false,

            // Comportamento do scroll nas extremidades.
            // TODO: Adicionar SelectControl "Comportamento das Bordas"
            // com opções 'trimSnaps' e 'keepSnaps'.
            containScroll: 'trimSnaps',

            // Observa o gesto de arrastar mesmo se o botão do rato não for
            // pressionado diretamente sobre o slider.
            // TODO: Adicionar ToggleControl "Observar Arraste Globalmente".
            watchDrag: true,
            
            // --- OPÇÕES RESPONSIVAS JÁ IMPLEMENTADAS ---
			breakpoints: {
                [`(min-width: ${BREAKPOINTS.medium}px)`]: {
                    slidesToScroll: parseInt(sliderNode.dataset.slidesToScrollMedium, 10) || 1,
                },
                [`(min-width: ${BREAKPOINTS.large}px)`]: {
                    slidesToScroll: parseInt(sliderNode.dataset.slidesToScrollLarge, 10) || 1,
                },
            },
		};

		const plugins = [];
		if (sliderNode.dataset.scrollMode === 'autoplay' && sliderNode.dataset.autoplay === 'true') {
			plugins.push(Autoplay({ delay: parseInt(sliderNode.dataset.autoplayDelay, 10) || 4000, stopOnInteraction: false, stopOnMouseEnter: true }));
		} else if (sliderNode.dataset.scrollMode === 'autoscroll') {
            const speed = parseFloat(sliderNode.dataset.autoScrollSpeed) || 1;
			plugins.push(AutoScroll({ playOnInit: true, stopOnInteraction: false, stopOnMouseEnter: true, speed: speed }));
		}

        // fade
        if (sliderNode.dataset.transitionEffect === 'fade') {
            plugins.push(Fade());
        }

        // autoheight
        if (sliderNode.dataset.autoHeight === 'true') {
            plugins.push(AutoHeight());
        } 

        // classnames
        plugins.push(ClassNames());

        // otimização das imagens
        applyInitialFetchPriority(sliderNode);

		// 2. Inicializa o Embla
		const emblaApi = EmblaCarousel(viewportNode, options, plugins);

        // --- ATIVAR O AUTO PAUSE DE VÍDEO ---
        // Adiciona a lógica para parar os vídeos ao trocar de slide
        // setupVideoAutopause(emblaApi);
        // ------------------------------------

        sliderNode.updateDots = () => updateDots(emblaApi, sliderNode);
        
		// --- LÓGICA DE CRIAÇÃO DA NAVEGAÇÃO ATUALIZADA ---
		let buttonsContainer, dotsContainer, thumbNav, fractionCounter;

        const manageNavigation = () => {
            // Verifica se deve mostrar as SETAS
            if (shouldShowNavigation(sliderNode, 'showNavigationButtons')) {
                if (!buttonsContainer) { 
                    buttonsContainer = document.createElement('div');
                    buttonsContainer.className = 'embla__buttons';
                    const prevBtn = document.createElement('button');
                    prevBtn.className = 'embla__button embla__button--prev';
                    prevBtn.setAttribute('type', 'button');
                    prevBtn.setAttribute('aria-label', __('Previous slide', 'wpmundi-blocks'));

                    const nextBtn = document.createElement('button');
                    nextBtn.className = 'embla__button embla__button--next';
                    nextBtn.setAttribute('type', 'button');
                    nextBtn.setAttribute('aria-label', __('Next slide', 'wpmundi-blocks'));

                    const iconSet = sliderNode.dataset.arrowIconSet || 'default';
                    const selectedSvgs = arrowSvgs[iconSet] || arrowSvgs.default;
                    prevBtn.innerHTML = selectedSvgs.prev;
                    nextBtn.innerHTML = selectedSvgs.next;                    
                    buttonsContainer.appendChild(prevBtn);
                    buttonsContainer.appendChild(nextBtn);
                    sliderNode.appendChild(buttonsContainer);

                    // lógica sem slider dinâmico
                    // setupButtons(emblaApi, prevBtn, nextBtn);

                    // lógica com slider dinâmico
                    // buttonSetupFunction(emblaApi, prevBtn, nextBtn, sliderNode, sliderNode.loadNextPageCallback);
                    const sliderType = sliderNode.dataset.sliderType;
                    if (sliderType === 'dynamic') {
                        // Para sliders dinâmicos, chama a função "mestre" que sabe o que fazer.
                        initializeDynamicSlider(emblaApi, prevBtn, nextBtn, sliderNode);
                    } else {
                        // Para sliders manuais, chama a função de configuração simples.
                        setupButtons(emblaApi, prevBtn, nextBtn);
                    }

                }
            } else {
                if (buttonsContainer) { 
                    buttonsContainer.remove();
                    buttonsContainer = null;
                }
            }           
           
            updateDots(emblaApi, sliderNode);    
		
        };

		// Roda a verificação quando a página carrega
        manageNavigation();

        // --- LÓGICA PARA A BARRA DE PROGRESSO ---
        if (shouldShowNavigation(sliderNode, 'showProgressBar')) {
            const progressContainer = document.createElement('div');
            progressContainer.className = 'embla__progress';
            progressContainer.innerHTML = '<div class="embla__progress__bar"></div>';
            sliderNode.appendChild(progressContainer);
            setupProgressBar(emblaApi, progressContainer);
        }
        
        // E roda a verificação novamente sempre que a janela for redimensionada
        window.addEventListener('resize', manageNavigation);


	});
};

// Garante que o script rode apenas quando a página estiver totalmente carregada
if (document.readyState === 'loading') {
	window.addEventListener('load', initializeWPMundiSliders);
} else {
	initializeWPMundiSliders();
}